import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import type { ModeDefinition, RuntimeConfig } from "@picnic-field/local-host/protocol";
import { ApiError } from "../../../lib/http/errors";
import { Button } from "../../../components/ui/Button";
import { ErrorBanner } from "../../../components/Shell";
import { useT } from "../../../i18n";
import { courseDeps } from "../../course";
import type { CourseViewLike } from "../../course";
import { lessonKey, STAGE_MODE_ID } from "../../stage";
import { Conflict, ValidationError } from "../api/opsApi";
import type { OpsApi } from "../api/opsApi";
import type { Issue, Json, ModeVersion } from "../types";
import {
  changeMeasure,
  changedPresets,
  checkDefinition,
  draftVersionOf,
  MEASURE_FIELDS,
  MEASURES,
  PAIR_FIELDS,
  patchPreset,
  PORTS,
  setGoalField,
  setTarget,
} from "../lib/stageGoals";
import type { Goal, GoalField, Measure } from "../lib/stageGoals";
import { modelJoints, stageBundle, stageHostFactory, startStageTest } from "../lib/stageTest";
import type { ModelJoint, StageAnswers, StageTestProgress, StageTestRun } from "../lib/stageTest";
import { fmtDate } from "./ConfigTab";
import { MbGoalsPanel } from "./MbGoalsPanel";
import { StagePicker } from "./StagePicker";
import type { StageProduct } from "./StagePicker";

/**
 * FS-15-08 기구 무대 목표(2026-10-08 · 6순위 '무대 목표 편집기') — 무대마다 시간 · 별 3단계 · 숨은 보너스의 잴 것 · 기준 · 화면 문구를 칸으로 고친다.
 *  - 시작: 기구 무대 모드(machine-stage)의 가장 큰 버전(배포 안 된 초안이 있으면 그것)
 *  - 검사: libs/modes validateModeDefinition(셸 · 커널과 같은 규칙) — 고칠 곳이 있으면 저장 · 배포를 막는다
 *  - 정답으로 시험: 편집 중인 정의로 그 차시의 Bench 정답을 실제 로컬 호스트(커널)에서 돌린다(빨리 감기 · 아이 손 일정 · 3D)
 *  - 저장: 새 버전 초안(배포된 버전은 고치지 않는다 — 마지막이 초안이면 덮는다) → 배포(새로 여는 무대부터) · 이전 버전 배포 = 되돌리기
 * 문구(label.ko · en)를 주면 학생 무대가 셸 번역(stage.lessons.*.gN)보다 먼저 쓴다.
 */
type StageOf = {
  lesson: string;
  model: string;
  scene: string;
  goals: Goal[];
  bonus?: Goal;
} & Record<string, unknown>;
type Def = {
  version: number;
  presets: ({ key: string; timerS: number; stage?: StageOf } & Record<string, unknown>)[];
} & Record<string, unknown>;
type Slot = 0 | 1 | 2 | "bonus";
const SPEEDS = [1, 2, 4] as const;

const asDef = (v: Json | null): Def | null =>
  v &&
  typeof v === "object" &&
  !Array.isArray(v) &&
  Array.isArray((v as { presets?: unknown }).presets)
    ? (JSON.parse(JSON.stringify(v)) as Def)
    : null;

export function StageGoalsTab({ api }: { api: OpsApi }) {
  // 제품(스마트브릭 기구 무대 · 마이크로비트 놀이터) — 무대 고르기(StagePicker) 위쪽 전환
  const [product, setProduct] = useState<StageProduct>("sb");
  return product === "sb" ? (
    <SbGoalsPanel api={api} onProduct={setProduct} />
  ) : (
    <MbGoalsPanel api={api} onProduct={setProduct} />
  );
}

/** 스마트브릭 기구 무대 목표(모드 정의 machine-stage 새 버전) */
function SbGoalsPanel({ api, onProduct }: { api: OpsApi; onProduct: (p: StageProduct) => void }) {
  const t = useT();
  const id = useId();
  const [versions, setVersions] = useState<ModeVersion[] | null>(null);
  const [base, setBase] = useState<Def | null>(null);
  const [baseVersion, setBaseVersion] = useState<number | null>(null);
  const [def, setDef] = useState<Def | null>(null);
  const [key, setKey] = useState<string | null>(null);
  const [issues, setIssues] = useState<{ preset: Issue[]; other: Issue[] }>({
    preset: [],
    other: [],
  });
  const [serverIssues, setServerIssues] = useState<Issue[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [answers, setAnswers] = useState<StageAnswers | null>(null);
  const [joints, setJoints] = useState<ModelJoint[]>([]);

  const load = useCallback(async () => {
    try {
      const modes = await api.modes();
      const vs = modes.find((m) => m.modeId === STAGE_MODE_ID)?.versions ?? [];
      setVersions(vs);
      const latest = [...vs].sort((a, b) => b.version - a.version)[0];
      if (!latest) return;
      const d = asDef(await api.modeDefinition(STAGE_MODE_ID, latest.version));
      setBase(d);
      setBaseVersion(latest.version);
      setDef(d);
    } catch (e) {
      setFailure(e instanceof ApiError ? e.code : "NET-SERVER");
    }
  }, [api]);
  useEffect(() => {
    void load();
  }, [load]);

  const presets = def?.presets.filter((p) => !!p.stage) ?? [];
  const pIndex = def && key ? def.presets.findIndex((p) => p.key === key) : -1;
  const preset = pIndex >= 0 ? def!.presets[pIndex]! : null;
  const changed = useMemo(() => new Set(changedPresets(base, def)), [base, def]);
  const titleOf = (k: string) => t(`stage.lessons.${lessonKey(k)}.title`);
  const live = versions?.find((v) => v.enabled) ?? null;
  const draft = versions ? draftVersionOf(versions) : null;

  // 검사(편집할 때마다)
  useEffect(() => {
    if (!def || pIndex < 0) return;
    let alive = true;
    void checkDefinition(def, pIndex).then((r) => alive && setIssues(r));
    return () => {
      alive = false;
    };
  }, [def, pIndex]);

  // 고른 무대 모형의 관절 목록(정답 사본과 함께 오는 자산 주소로)
  useEffect(() => {
    if (!preset?.stage) return;
    let alive = true;
    void (async () => {
      try {
        const a = answers ?? (await api.stageAnswers());
        if (!alive) return;
        if (!answers) setAnswers(a);
        const js = await modelJoints(a.assetBase, preset.stage!.model);
        if (alive) setJoints(js);
      } catch {
        if (alive) setJoints([]);
      }
    })();
    return () => {
      alive = false;
    };
    // answers 는 한 번 받으면 그대로
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, preset?.stage?.model]);

  const editPreset = (fn: (p: NonNullable<typeof preset>) => NonNullable<typeof preset>) => {
    if (!def || !key) return;
    setDef(patchPreset(def, key, fn));
    setNotice(null);
  };
  const editGoal = (slot: Slot, fn: (g: Goal) => Goal | undefined) =>
    editPreset((p) => {
      const st = { ...p.stage! };
      if (slot === "bonus") {
        const g = st.bonus ? fn(st.bonus) : undefined;
        if (g) st.bonus = g;
        else delete st.bonus;
      } else {
        const goals = [...st.goals];
        goals[slot] = fn(goals[slot]!) ?? goals[slot]!;
        st.goals = goals;
      }
      return { ...p, stage: st };
    });

  async function run(fn: () => Promise<string>) {
    setBusy(true);
    setFailure(null);
    setServerIssues([]);
    try {
      const msg = await fn();
      await load();
      setNotice(msg);
    } catch (e) {
      if (e instanceof ValidationError) setServerIssues(e.issues);
      else if (e instanceof Conflict) setFailure("HTTP-409");
      else setFailure(e instanceof ApiError ? e.code : "NET-SERVER");
    } finally {
      setBusy(false);
    }
  }

  const blocked = busy || !def || issues.preset.length + issues.other.length > 0;
  const save = (publishAfter: boolean) =>
    run(async () => {
      if (!def || !versions) return "";
      const [v] = draftVersionOf(versions);
      const next = { ...def, version: v };
      await api.saveMode(STAGE_MODE_ID, v, next as unknown as Json);
      if (publishAfter) await api.publishMode(STAGE_MODE_ID, v);
      return t(publishAfter ? "ops.goals.published" : "ops.goals.saved", { v });
    });

  return (
    <div className="pf-ops-goals" data-testid="ops-goals">
      <StagePicker
        product="sb"
        onProduct={onProduct}
        options={
          versions === null
            ? null
            : presets.map((p) => ({
                key: p.key,
                label: `${p.stage!.lesson} · ${titleOf(p.key)}`,
                ...(changed.has(p.key) ? { state: t("ops.goals.changed") } : {}),
              }))
        }
        value={key}
        onPick={(k) => {
          setKey(k);
          setNotice(null);
          setServerIssues([]);
        }}
        testId="goals-pick"
      />
      {failure && (
        <ErrorBanner
          cause={t("ops.common.saveFailed")}
          action={t("ops.common.retry")}
          code={failure}
        />
      )}
      <section className="pf-pane" aria-labelledby={`${id}-v`}>
        <h2 id={`${id}-v`} className="pf-h3">
          {t("ops.goals.title")}
        </h2>
        <p className="pf-muted" data-testid="goals-versions-sum">
          {live ? t("ops.goals.versionLive", { v: live.version }) : ""}
          {draft?.[1] ? ` · ${t("ops.goals.versionDraft", { v: draft[0] })}` : ""}
          {baseVersion != null ? ` · v${baseVersion}` : ""}
        </p>
        {versions && versions.length > 0 && (
          <details>
            <summary>{t("ops.goals.versions")}</summary>
            <table className="pf-tbl" data-testid="goals-versions">
              <thead>
                <tr>
                  <th scope="col">{t("ops.goals.colVersion")}</th>
                  <th scope="col">{t("ops.goals.colState")}</th>
                  <th scope="col">
                    <span className="pf-sr-only">{t("teacher.nick.colAction")}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {[...versions]
                  .sort((a, b) => b.version - a.version)
                  .map((v) => (
                    <tr key={v.version} data-version={v.version}>
                      <th scope="row">v{v.version}</th>
                      <td>
                        {v.enabled
                          ? t("ops.goals.live")
                          : v.publishedAt
                            ? t("ops.goals.off")
                            : t("ops.goals.draft")}
                        {v.publishedAt ? ` · ${fmtDate(v.publishedAt)}` : ""}
                      </td>
                      <td>
                        {!v.enabled && (
                          <Button
                            variant="ghost"
                            disabled={busy}
                            data-testid={`goals-rollback-${v.version}`}
                            onClick={() =>
                              void run(async () => {
                                await api.publishMode(STAGE_MODE_ID, v.version);
                                return t("ops.goals.rolledBack", { v: v.version });
                              })
                            }
                          >
                            {t("ops.goals.rollback")}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </details>
        )}
      </section>

      <div>
        <section className="pf-pane" aria-labelledby={`${id}-e`}>
          {!preset?.stage ? (
            <>
              <h2 id={`${id}-e`} className="pf-h3">
                {t("ops.goals.title")}
              </h2>
              <p className="pf-muted">{t("ops.goals.pick")}</p>
            </>
          ) : (
            <>
              <h2 id={`${id}-e`} className="pf-h3" data-testid="goals-head">
                {t("ops.goals.head", { lesson: preset.stage.lesson, title: titleOf(preset.key) })}
              </h2>
              <p className="pf-muted">
                {t("ops.goals.model", { model: preset.stage.model, scene: preset.stage.scene })}
              </p>
              <label className="pf-row">
                {t("ops.goals.timer")}{" "}
                <input
                  type="number"
                  min={10}
                  max={600}
                  step={5}
                  value={preset.timerS}
                  data-testid="goals-timer"
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (Number.isFinite(v)) editPreset((p) => ({ ...p, timerS: v }));
                  }}
                />
              </label>
              <datalist id={`${id}-joints`}>
                {joints.map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.port ? `${j.id} (${j.port})` : j.id}
                  </option>
                ))}
              </datalist>
              <div className="pf-ops-goals__cards">
                {([0, 1, 2] as const).map((i) => (
                  <GoalCard
                    key={`${preset.key}-${i}`}
                    title={t(`ops.goals.star${i + 1}`)}
                    goal={preset.stage!.goals[i]!}
                    defaultText={t(`stage.lessons.${lessonKey(preset.key)}.g${i + 1}`)}
                    jointsList={`${id}-joints`}
                    onChange={(fn) => editGoal(i, fn)}
                    testId={`goal-${i}`}
                  />
                ))}
                <div className="pf-ops-goals__bonus">
                  <label>
                    <input
                      type="checkbox"
                      checked={!!preset.stage.bonus}
                      data-testid="goal-bonus-on"
                      onChange={(e) =>
                        editPreset((p) => {
                          const st = { ...p.stage! };
                          if (e.target.checked)
                            st.bonus = base?.presets.find((b) => b.key === p.key)?.stage?.bonus ?? {
                              measure: "beeps",
                              n: 1,
                            };
                          else delete st.bonus;
                          return { ...p, stage: st };
                        })
                      }
                    />{" "}
                    {t("ops.goals.bonusOn")}
                  </label>
                  {preset.stage.bonus && (
                    <GoalCard
                      title={t("ops.goals.bonus")}
                      goal={preset.stage.bonus}
                      defaultText={t(`stage.lessons.${lessonKey(preset.key)}.bonus`)}
                      jointsList={`${id}-joints`}
                      onChange={(fn) => editGoal("bonus", fn)}
                      testId="goal-bonus"
                    />
                  )}
                </div>
              </div>

              <p
                className={issues.preset.length ? "pf-ops-stages__warn" : "pf-muted"}
                role="status"
                data-testid="goals-issues"
              >
                {issues.preset.length
                  ? t("ops.goals.issues", { n: issues.preset.length })
                  : t("ops.goals.ok")}
                {issues.other.length > 0 &&
                  ` · ${t("ops.goals.otherIssues", { n: issues.other.length })}`}
              </p>
              {[...issues.preset, ...serverIssues].length > 0 && (
                <ul className="pf-ops-stages__issues">
                  {[...issues.preset, ...serverIssues].slice(0, 10).map((i) => (
                    <li key={i.path + i.message}>
                      <code>{i.path}</code> {i.message}
                    </li>
                  ))}
                </ul>
              )}

              <StageTestPanel
                api={api}
                def={def!}
                presetKey={preset.key}
                lesson={preset.stage.lesson}
                scene={preset.stage.scene}
                answers={answers}
                onAnswers={setAnswers}
                disabled={issues.preset.length > 0}
              />

              <div className="pf-row pf-ops-stages__save">
                <Button
                  variant="ghost"
                  disabled={busy || !changed.has(preset.key)}
                  onClick={() => {
                    const orig = base?.presets.find((p) => p.key === preset.key);
                    if (orig) editPreset(() => JSON.parse(JSON.stringify(orig)));
                  }}
                >
                  {t("ops.goals.resetPreset")}
                </Button>
                <Button
                  disabled={blocked}
                  onClick={() => void save(false)}
                  data-testid="goals-save"
                >
                  {t("ops.goals.saveDraft")}
                </Button>
                <Button
                  variant="accent"
                  disabled={blocked}
                  onClick={() => void save(true)}
                  data-testid="goals-publish"
                >
                  {t("ops.goals.publish")}
                </Button>
              </div>
              {notice && (
                <p className="pf-note" role="status" data-testid="goals-notice">
                  {notice}
                </p>
              )}
              <p className="pf-note">{t("ops.goals.note")}</p>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

/** 목표 하나 — 잴 것 · 대상(관절 · 포트) · 기준 칸 · 문구 */
function GoalCard({
  title,
  goal,
  defaultText,
  jointsList,
  onChange,
  testId,
}: {
  title: string;
  goal: Goal;
  defaultText: string;
  jointsList: string;
  onChange: (fn: (g: Goal) => Goal | undefined) => void;
  testId: string;
}) {
  const t = useT();
  const spec = MEASURE_FIELDS[goal.measure] ?? MEASURE_FIELDS.turns;
  const targetKind =
    typeof goal.port === "string" ? "port" : typeof goal.joint === "string" ? "joint" : "none";
  const num = (v: string) => (v.trim() === "" ? undefined : Number(v));
  const label = (goal.label ?? {}) as { ko?: string; en?: string };
  const setLabel = (lang: "ko" | "en", v: string) =>
    onChange((g) => {
      const next = { ...label, [lang]: v.trim() || undefined };
      if (!next.ko) delete next.ko;
      if (!next.en) delete next.en;
      return setGoalField(g, "label", Object.keys(next).length ? next : undefined);
    });
  const field = (f: GoalField) => {
    const name = t(`ops.goals.fields.${f}`);
    if (f === "growing")
      return (
        <label key={f}>
          <input
            type="checkbox"
            checked={goal.growing === true}
            onChange={(e) => onChange((g) => setGoalField(g, f, e.target.checked))}
          />{" "}
          {name}
        </label>
      );
    if (f === "sequence")
      return (
        <label key={f}>
          {name}{" "}
          <input
            value={Array.isArray(goal.sequence) ? (goal.sequence as string[]).join(" ") : ""}
            onChange={(e) =>
              onChange((g) =>
                setGoalField(
                  g,
                  f,
                  e.target.value
                    .split(/[\s,]+/)
                    .map((x) => x.trim().toUpperCase())
                    .filter(Boolean),
                ),
              )
            }
          />
        </label>
      );
    if (PAIR_FIELDS.includes(f)) {
      const pair = (Array.isArray(goal[f]) ? goal[f] : []) as number[];
      const set = (i: 0 | 1, v: string) =>
        onChange((g) => {
          const cur = (Array.isArray(g[f]) ? [...(g[f] as number[])] : [undefined, undefined]) as (
            number | undefined
          )[];
          cur[i] = num(v);
          return setGoalField(
            g,
            f,
            cur[0] === undefined && cur[1] === undefined ? undefined : [cur[0] ?? 0, cur[1] ?? 0],
          );
        });
      return (
        <label key={f}>
          {name}{" "}
          <input
            type="number"
            value={pair[0] ?? ""}
            onChange={(e) => set(0, e.target.value)}
            aria-label={`${name} min`}
          />
          ~
          <input
            type="number"
            value={pair[1] ?? ""}
            onChange={(e) => set(1, e.target.value)}
            aria-label={`${name} max`}
          />
        </label>
      );
    }
    return (
      <label key={f}>
        {name}{" "}
        <input
          type="number"
          step="any"
          value={typeof goal[f] === "number" ? (goal[f] as number) : ""}
          data-field={f}
          onChange={(e) => onChange((g) => setGoalField(g, f, num(e.target.value)))}
        />
      </label>
    );
  };
  return (
    <fieldset className="pf-ops-stages__box pf-ops-goals__card" data-testid={testId}>
      <legend>{title}</legend>
      <div className="pf-ops-stages__form">
        <label>
          {t("ops.goals.measure")}{" "}
          <select
            value={goal.measure}
            data-testid={`${testId}-measure`}
            onChange={(e) => onChange((g) => changeMeasure(g, e.target.value as Measure))}
          >
            {MEASURES.map((m) => (
              <option key={m} value={m}>
                {t(`ops.goals.measures.${m}`)}
              </option>
            ))}
          </select>
        </label>
        {goal.measure !== "notes" && (
          <label>
            {t("ops.goals.target")}{" "}
            <select
              value={targetKind}
              onChange={(e) =>
                onChange((g) =>
                  setTarget(
                    g,
                    e.target.value as "joint" | "port" | "none",
                    e.target.value === "port" ? "A" : "",
                  ),
                )
              }
            >
              {!spec.target && <option value="none">{t("ops.goals.targetNone")}</option>}
              <option value="joint">{t("ops.goals.targetJoint")}</option>
              <option value="port">{t("ops.goals.targetPort")}</option>
            </select>{" "}
            {targetKind === "joint" && (
              <input
                list={jointsList}
                value={typeof goal.joint === "string" ? goal.joint : ""}
                size={12}
                onChange={(e) => onChange((g) => setTarget(g, "joint", e.target.value.trim()))}
              />
            )}
            {targetKind === "port" && (
              <select
                value={goal.port as string}
                onChange={(e) => onChange((g) => setTarget(g, "port", e.target.value))}
              >
                {PORTS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            )}
          </label>
        )}
        {spec.fields.map(field)}
      </div>
      <div className="pf-ops-stages__form">
        <label>
          {t("ops.goals.labelKo")}{" "}
          <input
            value={label.ko ?? ""}
            maxLength={80}
            size={32}
            placeholder={defaultText}
            data-testid={`${testId}-label-ko`}
            onChange={(e) => setLabel("ko", e.target.value)}
          />
        </label>
        <label>
          {t("ops.goals.labelEn")}{" "}
          <input
            value={label.en ?? ""}
            maxLength={80}
            size={24}
            onChange={(e) => setLabel("en", e.target.value)}
          />
        </label>
      </div>
    </fieldset>
  );
}

/** 정답으로 시험 — 편집 중인 정의로 실제 로컬 호스트에서 정답을 돌리고(빨리 감기) 3D · 별 · 목표 값을 보인다 */
function StageTestPanel({
  api,
  def,
  presetKey,
  lesson,
  scene,
  answers,
  onAnswers,
  disabled,
}: {
  api: OpsApi;
  def: Def;
  presetKey: string;
  lesson: string;
  scene: string;
  answers: StageAnswers | null;
  onAnswers: (a: StageAnswers) => void;
  disabled: boolean;
}) {
  const t = useT();
  const boxRef = useRef<HTMLDivElement>(null);
  const runRef = useRef<{
    run: StageTestRun | null;
    view: CourseViewLike | null;
    canvas: HTMLCanvasElement | null;
  }>({
    run: null,
    view: null,
    canvas: null,
  });
  const [speed, setSpeed] = useState<(typeof SPEEDS)[number]>(4);
  /** 3D 를 끄면 시뮬레이션만 돌아 더 빠르다(느린 PC) */
  const [show3d, setShow3d] = useState(true);
  const [state, setState] = useState<"idle" | "loading" | "running" | "done" | "fail">("idle");
  const [msg, setMsg] = useState<string | null>(null);
  const [progress, setProgress] = useState<StageTestProgress | null>(null);

  const stop = useCallback(() => {
    const r = runRef.current;
    r.run?.stop();
    r.view?.dispose();
    r.canvas?.remove();
    runRef.current = { run: null, view: null, canvas: null };
  }, []);
  // 무대를 바꾸거나 떠나면 멈춘다
  useEffect(() => {
    stop();
    setState("idle");
    setProgress(null);
    return stop;
  }, [presetKey, stop]);

  async function start() {
    stop();
    setState("loading");
    setMsg(null);
    setProgress(null);
    try {
      const a = answers ?? (await api.stageAnswers());
      if (!answers) onAnswers(a);
      const ans = a.lessons[lesson];
      if (!ans) throw new Error(t("ops.goals.noAnswer"));
      const preset = def.presets.find((p) => p.key === presetKey)!;
      const [bundle, cfg] = await Promise.all([
        stageBundle(a.assetBase, preset.stage!.model, ans.program),
        api.runtimeConfig(),
      ]);
      const run = await startStageTest({
        createHost: stageHostFactory(),
        def: def as unknown as ModeDefinition,
        presetKey,
        bundle,
        runtimeConfig: (cfg?.config ?? {}) as unknown as RuntimeConfig,
        speed,
      });
      runRef.current.run = run;
      // 3D — 마운트마다 새 캔버스(같은 캔버스에 렌더러 둘이 붙지 않게) · 끄면 그리지 않는다
      const canvas = show3d ? document.createElement("canvas") : null;
      if (canvas) {
        canvas.className = "pf-ops-goals__canvas";
        boxRef.current?.prepend(canvas);
        runRef.current.canvas = canvas;
      }
      if (canvas)
        void courseDeps()
          .createView(canvas, {
            bodies: run.load.bodies,
            followIndex: run.load.followIndex,
            me: { glbUrl: bundle.asset.glbUrl, appearance: null, palette: null },
            stageScene: scene,
            follow: { back: -0.95, up: 0.4, side: 0.18, lookAhead: 0, stiffness: 3 },
          })
          .then((v) => {
            if (runRef.current.run !== run) return v.dispose();
            runRef.current.view = v;
            run.onFrame((f) => v.update(f));
          })
          .catch(() => {});
      run.onProgress((p) => {
        setProgress(p);
        if (p.done) setState("done");
      });
      setState("running");
    } catch (e) {
      stop();
      setState("fail");
      setMsg(t("ops.goals.testFail", { msg: e instanceof Error ? e.message : String(e) }));
    }
  }

  const s1 = (v: number) => v.toFixed(1);
  return (
    <fieldset className="pf-ops-stages__box pf-ops-goals__test" data-testid="goals-test">
      <legend>{t("ops.goals.test")}</legend>
      <div className="pf-row pf-row--wrap">
        <label>
          {t("ops.goals.speed")}{" "}
          <select
            value={speed}
            onChange={(e) => setSpeed(Number(e.target.value) as (typeof SPEEDS)[number])}
          >
            {SPEEDS.map((s) => (
              <option key={s} value={s}>
                ×{s}
              </option>
            ))}
          </select>
        </label>
        <label>
          <input type="checkbox" checked={show3d} onChange={(e) => setShow3d(e.target.checked)} />{" "}
          {t("ops.goals.show3d")}
        </label>
        {state === "running" || state === "loading" ? (
          <Button
            variant="ghost"
            onClick={() => {
              stop();
              setState("idle");
            }}
          >
            {t("ops.goals.testStop")}
          </Button>
        ) : (
          <Button disabled={disabled} onClick={() => void start()} data-testid="goals-test-start">
            {t("ops.goals.test")}
          </Button>
        )}
      </div>
      <div ref={boxRef} className="pf-ops-goals__view" data-state={state}>
        {state === "loading" && <p className="pf-muted">{t("ops.goals.testLoading")}</p>}
        {msg && <p className="pf-ops-stages__warn">{msg}</p>}
      </div>
      {progress && (
        <div data-testid="goals-test-progress" data-stars={progress.stars}>
          <p>
            <strong>
              {t("ops.goals.testResult", { stars: progress.stars, s: s1(progress.simS) })}
            </strong>
            {state === "done" && ` · ${t("ops.goals.testDone", { stars: progress.stars })}`}
          </p>
          <ul className="pf-ops-stages__issues">
            {[0, 1, 2].map((i) => (
              <li key={i}>
                {progress.starAt[i] !== undefined
                  ? t("ops.goals.starAt", { i: i + 1, s: s1(progress.starAt[i]!) })
                  : t("ops.goals.notYet", { i: i + 1 })}
                {progress.goals[i] &&
                  ` · ${t("ops.goals.goalValue", {
                    measure: t(`ops.goals.measures.${progress.goals[i]!.measure}`),
                    value: Math.round(progress.goals[i]!.value * 10) / 10,
                    target: progress.goals[i]!.target,
                  })}`}
              </li>
            ))}
            {progress.bonus && (
              <li>
                🎁 {progress.bonus.done ? "✓" : "…"} {Math.round(progress.bonus.value * 10) / 10}/
                {progress.bonus.target}
              </li>
            )}
          </ul>
        </div>
      )}
    </fieldset>
  );
}
